"use client";

/**
 * CollabV3AppCta 컴포넌트
 * @description 웹 3.0 상세의 유일한 행동 — 앱에서 이 콜랩을 연다. **웹은 보기 전용이다.** 저요(무료 참여
 *   포함) · 예약 · 결제 · 모금 · 작품 올리기 · 글쓰기 · 하트 · 취소 · 주최자 동작을 웹에 두지 않는다.
 *   앱이 참여 버튼을 그릴 자리에 이 버튼 하나만 선다. 서버의 `actionBar`(웹 요청이면 `APP_ONLY`)도
 *   같은 뜻이다.
 *
 *   - 모으는 중 · 진행 중: `Continue in the app` — 2.0 웹 상세가 "참여는 앱에서" 일 때 쓰는 문구
 *   - 끝남: `This collab has ended` + `See how it went`(2.0 웹 상세와 같다)
 *   - 취소: `This collab was cancelled` + `Open in app`
 *
 *   경로는 `/collabs/{id}` 이고 앱 설치 배너와 같은 딥링크(`openInApp`)라, 앱이 없으면 스토어로 간다.
 *   계측은 2.0 하단 CTA 와 같은 `Collab App Opened`(`placement: "cta"`)다.
 *   껍데기(폰은 화면 바닥 고정, 데스크톱은 곁 카드 맨 아래)는 예전 2.0 하단 CTA 와 같다.
 * @example <CollabV3AppCta subject={subject} badge={detail.badge} />
 */
import { Button } from "@fanwork-web/design-system";
import { memo, useMemo } from "react";

import { collabV3CtaStyles as styles } from "./CollabV3Cta.styles";
import type { CollabV3AppCtaProps } from "./CollabV3Detail.types";
import { useCollabV3OpenApp } from "./useCollabV3OpenApp";

import { COLLAB_V3_COPY } from "../../constants/collabV3Copy";
import type { CollabV3StatusBadge } from "../../dtos";
import { collabV3AppPaths } from "../../utils/collabV3AppPaths";

const { app, bar } = COLLAB_V3_COPY;

interface CtaContent {
  notice: string | null;
  action: string;
}

function contentOf(badge: CollabV3StatusBadge): CtaContent {
  if (badge === "ENDED") {
    return { notice: bar.legacyEnded, action: app.seeInApp };
  }
  if (badge === "CANCELLED") {
    return { notice: bar.cancelledNoReason, action: app.openApp };
  }
  return { notice: null, action: app.continueInApp };
}

const CollabV3AppCta = memo<CollabV3AppCtaProps>(({ subject, badge }) => {
  const path = useMemo(
    () => collabV3AppPaths(subject.collabId).detail,
    [subject.collabId],
  );
  const open = useCollabV3OpenApp(subject, "cta", path);
  const { notice, action } = contentOf(badge);

  return (
    <div className={styles.shell}>
      {notice ? <p className={styles.notice}>{notice}</p> : null}
      <Button
        text={action}
        tone="brand"
        size="md"
        radius="circle"
        fullWidth
        onClick={open}
      />
    </div>
  );
});

CollabV3AppCta.displayName = "CollabV3AppCta";

export default CollabV3AppCta;
